import { ReactNode, useCallback, useEffect, useState } from 'react';
import { Dialog, Grid, Button, Stack } from '@mui/material';
import Avatar from 'components/@extended/Avatar';
import { Refresh, Slash } from 'iconsax-react';
import serverCall from 'api/serverCall';
import api from 'api/api';
import { PopupTransition } from 'components/@extended/Transitions';
import ModifyOrder from './components/ModifyOrder';
import { IOrderV2, OrderSearchResponse } from 'types/custom/order';
import { useDispatch } from 'react-redux';
import { openSnackbar } from 'store/reducers/snackbar';
// import OrdersPageShimmer from 'shimmers/pages/OrdersPageShimmer';
import { useWebsocketContext } from 'contexts/WebSocketContext';
import { PaginationState } from 'types/custom/common';
import OrdersTableV2 from './components/OrdersTableV2';
import { OrderType } from 'enum/order-type';
import { Typography } from '@mui/material';
import Alert from 'components/alert/CommonAlert';
import OrderAgainModalV2 from 'components/common/OrderAgainModalV2';
// ==============================|| Stock - LIST ||============================== //

export type ValidationError = { stock: string; quantity: string };

const OrdersPageV2 = () => {
  const [orders, setOrders] = useState<IOrderV2[]>([]);
  const [currentOrder, setCurrentOrder] = useState<IOrderV2 | null>(null);
  const [modifyOrder, setModifyOrder] = useState<boolean>(false);
  const [loading, setLoading] = useState<boolean>(false);
  const [refreshing, setRefreshing] = useState<boolean>(false);
  const [page, setPage] = useState<PaginationState>({ page: 0, limit: 10 });
  const [totalOrders, setTotalOrders] = useState<number>(0);
  const dispatch = useDispatch();
  //@ts-ignore
  const [fadedRow, setFadedRow] = useState<number | null>(null);
  const [openPlaceOrderModal, setOpenPlaceOrderModal] = useState<boolean>(false);
  const [orderType, setOrderType] = useState<OrderType | null>(null);
  const [openCancelOrder, setOpenCancelOrder] = useState<boolean>(false);
  const [action, setAction] = useState<string>('');

  const { orderStatusChanged } = useWebsocketContext();

  const fetchAllOrders = useCallback(() => {
    setLoading(true);
    return new Promise((resolve, reject) => {
      serverCall.get(api.getAllOrders(page.page, page.limit ?? 5), (response: OrderSearchResponse, error) => {
        if (error) {
          console.error(error);
          setLoading(false);
          reject(error);
        } else {
          setOrders(response.searchResponse as IOrderV2[]);
          setTotalOrders(response?.totalNoRecords);
          setLoading(false);
          resolve(response);
        }
      });
    });
  }, [page, orderStatusChanged]);

  useEffect(() => {
    fetchAllOrders().catch(() => {
      console.error('Error fetching dashboard orders');
    });
  }, [orderStatusChanged, page]);
  const handleCloseModifyModal = () => {
    setModifyOrder(false);
  };

  const handleRefreshData = () => {
    setRefreshing(true);
    fetchAllOrders()
      .then(() => {
        dispatch(
          openSnackbar({
            open: true,
            message: 'Data Refreshed Successfully',
            variant: 'alert',
            alert: {
              color: 'success'
            },
            close: true,
            anchorOrigin: {
              vertical: 'top',
              horizontal: 'center'
            }
          })
        );
      })
      .catch(() => {
        dispatch(
          openSnackbar({
            open: true,
            message: 'Data refresh failed..',
            variant: 'alert',
            alert: {
              color: 'error'
            },
            close: true,
            anchorOrigin: {
              vertical: 'top',
              horizontal: 'center'
            }
          })
        );
      })
      .finally(() => {
        setRefreshing(false);
      });
  };

  const handleCloseCancelOrderModal = () => {
    setOpenCancelOrder(false);
  };

  const handleCancelOrder = () => {
    const orderId = currentOrder?.orderId?.toString();
    if (!orderId) {
      return;
    }
    serverCall.put(api.cancelOrder(orderId!), {}, (response, error: any) => {
      if (error) {
        console.error(error);
        const errorMessage = error?.response?.data?.message;
        dispatch(
          openSnackbar({
            open: true,
            close: true,
            message: errorMessage ?? 'Failed to cancel the order',
            variant: 'alert',
            alert: {
              color: 'error'
            },
            anchorOrigin: {
              vertical: 'top',
              horizontal: 'center'
            }
          })
        );
      } else {
        if (response) {
          fetchAllOrders();
          dispatch(
            openSnackbar({
              open: true,
              close: true,
              message: 'Order canceled Successfully',
              variant: 'alert',
              alert: {
                color: 'success'
              },
              anchorOrigin: {
                vertical: 'top',
                horizontal: 'center'
              }
            })
          );
          setOpenCancelOrder(false);
        }
      }
    });
  };

  const handleClosePlaceOrderModal = (clearData: boolean) => {
    setOpenPlaceOrderModal(false);
    if (clearData) {
      //   setPlaceOrderData(null);
      setOrderType(null);
    }
  };

  const alertContent: ReactNode = (
    <Stack alignItems="center" spacing={3.5}>
      <Avatar color="error" sx={{ width: 72, height: 72, fontSize: '1.75rem' }}>
        <Slash variant="Bold" />
      </Avatar>
      <Stack spacing={2}>
        <Typography variant="h4" align="center">
          Confirm Order Cancellation
        </Typography>
        <Typography align="center">Are you sure you want to cancel this order?</Typography>
      </Stack>

      <Stack direction="row" spacing={2} sx={{ width: 1 }}>
        <Button fullWidth onClick={handleCloseCancelOrderModal} color="secondary" variant="outlined">
          No, Keep Order
        </Button>
        <Button fullWidth color="primary" variant="contained" onClick={handleCancelOrder} autoFocus>
          Yes, Cancel Order
        </Button>
      </Stack>
    </Stack>
  );

  // if (loading && orders.length === 0) {
  //   return <OrdersPageShimmer />;
  // }

  return (
    <>
      <Alert open={openCancelOrder} handleClose={handleCloseCancelOrderModal} content={alertContent} />
      <Grid container justifyContent="end" alignItems="center" mb={2}>
        <Button color="info" onClick={handleRefreshData} variant="contained" startIcon={<Refresh />}>
          {refreshing ? 'Refreshing...' : 'Refresh'}
        </Button>
      </Grid>
      <OrdersTableV2
        data={orders ? orders : []}
        handleRefreshData={handleRefreshData}
        page={page}
        setPage={setPage}
        totalRecords={totalOrders}
        setFadedRow={setFadedRow}
        setOrder={setCurrentOrder}
        setOrderType={setOrderType}
        setOpenPlaceOrderModal={setOpenPlaceOrderModal}
        loading={loading}
        handleCancelOrder={() => {
          setOpenCancelOrder(true);
        }}
        setAction={setAction}
      />
      <Dialog
        maxWidth="sm"
        fullWidth
        TransitionComponent={PopupTransition}
        onClose={handleCloseModifyModal}
        open={modifyOrder}
        sx={{ '& .MuiDialog-paper': { p: 0 } }}
      >
        <ModifyOrder order={currentOrder} fetchAllOrders={fetchAllOrders} onCancel={handleCloseModifyModal} />
      </Dialog>
      {openPlaceOrderModal && (
        <OrderAgainModalV2
          fetchAllOrders={fetchAllOrders}
          orderType={orderType}
          handleClose={handleClosePlaceOrderModal}
          open={openPlaceOrderModal}
          key={'place-order-modal'}
          order={currentOrder}
          action={action}
        />
      )}
    </>
  );
};

export default OrdersPageV2;
